import React, { useEffect, useState, useRef } from "react";
import { Box, Paper, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { colors } from "../ui/colors";
import { Job } from "./JobsWidgetWithWeek";
import { enUS } from "date-fns/locale";
import { format, startOfWeek, addDays, isSameDay } from "date-fns";
import { CancelPresentation } from "@mui/icons-material";
import { useWidgetVariant } from "./useWidgetVariant";
import { jobOverlapsDay } from "./utils/jobOverlapsRange";

// --- Interfaces ---

interface Prop {
  selectedJob: string;
  // jobs: Job[];
  jobs: any[];
  onDayClick: (day: Date) => void;
  activeDay: Date;
  referenceDate?: Date;
  timeZone?: string;
}
interface DayColumnProps {
  isOver?: boolean;
}

const DayColumn = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isWeekend" && prop !== "isOver",
})<DayColumnProps>(({ isOver }) => ({
  flex: 1,
  minWidth: "80px",
  minHeight: "100px",
  backgroundColor: isOver ? "var(--fx-colors-warning-50, #FFF7ED)" : "var(--fx-surface-base, #FFFFFF)",
  borderRight: "1px solid var(--fx-surface-border, #E2E8F0)",
  transition: "all 0.2s ease",
  display: "flex",
  flexDirection: "column",
  "&:last-child": { borderRight: "none" },
}));

export const ItemWeek = ({ color, count, mini = false }) => {
  return (
    <Box
      sx={{
        borderRadius: "50%",
        bgcolor: color,
        width: mini ? "8px" : "20px",
        height: mini ? "8px" : "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      {mini ? null : (
        <Typography
          variant="caption"
          sx={{
            color: "white",
            fontWeight: 800,
            fontSize: "0.7rem",
            lineHeight: 1,
          }}
        >
          {count}
        </Typography>
      )}
    </Box>
  );
};

export const WeekJobs: React.FC<Prop> = ({ selectedJob, jobs, onDayClick, activeDay, referenceDate = new Date(), timeZone }) => {
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(referenceDate, i));
  const currentFilter = (selectedJob || "all").toLowerCase();
  const { ref, isLargeSlot, width } = useWidgetVariant(500);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  return (
    <Box ref={ref} sx={{ width: isLargeSlot ? "100%" : "auto", height: isLargeSlot ? "100%" : "132px", mt: 1 }}>
      <Paper
        ref={scrollContainerRef}
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid var(--fx-surface-border, #E2E8F0)",
          overflow: "hidden",
          width: "100%",
          maxHeight: isLargeSlot ? "100%" : "350px",
        }}
      >
        <Box sx={{ display: "flex", overflowX: "auto", minHeight: isLargeSlot ? "100%" : "140px" }}>
          {weekDays.map((day) => {
            const isSelected = activeDay && isSameDay(day, activeDay);
            const jobsInThisDay = jobs.filter((j) => jobOverlapsDay(j, day, timeZone));
            const resCount = jobsInThisDay.filter((j) => j.type === "residential").length;
            const comCount = jobsInThisDay.filter((j) => j.type === "commercial").length;

            return (
              <DayColumn
                key={day.toString()}
                onClick={() => onDayClick(day)}
                sx={{
                  cursor: "pointer",
                  backgroundColor: isSelected ? "var(--fx-colors-warning-50, #FFF7ED)" : "var(--fx-surface-base, #FFFFFF)",
                  borderBottom: isSelected ? `3px solid ${colors.orange.base}` : "3px solid transparent",

                  transition: "all 0.3s ease",
                  "&:hover": { bgcolor: "var(--fx-surface-sunken, #F8FAFC)" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    textAlign: "center",
                    borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
                    py: 0.75,
                    lineHeight: 1,
                    bgcolor: colors.orange[50],
                  }}
                >
                  <Typography variant="caption" color={colors.orange.base} fontWeight={800} sx={{ fontSize: "0.65rem", textTransform: "uppercase" }}>
                    {format(day, "EEE", { locale: enUS })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
                    {format(day, "MMM d")}
                  </Typography>
                </Box>

                <Box sx={{ p: 2, display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
                  <Box display="flex" gap={0.5} alignItems="center">
                    {(currentFilter === "all" || currentFilter === "commercial") &&
                      (comCount > 0 ? (
                        <ItemWeek color={colors.info.base} count={comCount} />
                      ) : (
                        <Box sx={{ color: "#e2e8f0", display: "flex" }}>
                          <CancelPresentation sx={{ fontSize: 16 }} />
                        </Box>
                      ))}

                    {(currentFilter === "all" || currentFilter === "residential") &&
                      (resCount > 0 ? (
                        <ItemWeek color={colors.orange.base} count={resCount} />
                      ) : (
                        <Box sx={{ color: "#e2e8f0", display: "flex" }}>
                          <CancelPresentation sx={{ fontSize: 16 }} />
                        </Box>
                      ))}
                  </Box>
                </Box>
              </DayColumn>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};
